import type { ScheduleEvent } from "@/lib/types";

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
const MAX_VISIBLE = 3;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function dayLabel(day: string): string {
  const parts = day.split("-");
  if (parts.length < 3) return day;
  return `${Number(parts[1])}월 ${Number(parts[2])}일`;
}

function isLunar(ev: ScheduleEvent): boolean {
  return Number(ev.is_lunar) === 1;
}

function barColor(ev: ScheduleEvent): string {
  if (ev.color) return ev.color;
  return ev.kind === "birth" ? "#f7a81b" : "#1e5aa8";
}

// 일정(색 막대) / 생일(연노랑 막대) 을 한 줄로 표현한다.
function EventBar({ ev }: { ev: ScheduleEvent }) {
  if (ev.kind === "birth") {
    return (
      <span className="flex items-center gap-1 truncate rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-secondary-900">
        <i className="ri-cake-3-line shrink-0" aria-hidden="true"></i>
        <span className="truncate">
          {ev.name}
          {isLunar(ev) ? "(음)" : ""}
        </span>
      </span>
    );
  }

  return (
    <span
      className="flex items-center gap-1 truncate rounded px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-background-50"
      style={{ backgroundColor: barColor(ev) }}
    >
      {ev.time && <span className="shrink-0 tabular-nums">{ev.time}</span>}
      <span className="truncate text-left">{ev.title || "일정"}</span>
    </span>
  );
}

interface ScheduleCalendarProps {
  ym: string; // "YYYY-MM"
  events: ScheduleEvent[];
  attendByDay: Record<string, number>;
  myDays: string[];
  today?: string;
  expandedDay: string | null;
  onToggleDay: (day: string) => void;
}

// 일정 + 생일 + 출근도장을 한 달력에 합쳐 보여준다.
// 일정·생일 막대가 3개를 넘으면 «+N» 으로 접고, 날짜를 누르면 아래에 그날 목록이 펼쳐진다.
export default function ScheduleCalendar({
  ym,
  events,
  attendByDay,
  myDays,
  today,
  expandedDay,
  onToggleDay,
}: ScheduleCalendarProps) {
  const [yearStr, monthStr] = ym.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!year || !month) return null;

  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const mySet = new Set(myDays);

  // 도장(kind:"attend")은 attend.by_day 로 인원수를 따로 받으므로 막대에서는 제외한다.
  const byDay: Record<string, ScheduleEvent[]> = {};
  events.forEach((ev) => {
    if (ev.date && ev.kind !== "attend") {
      if (!byDay[ev.date]) byDay[ev.date] = [];
      byDay[ev.date].push(ev);
    }
  });

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const expandedDate = expandedDay ?? "";
  const expandedEvents = expandedDay ? byDay[expandedDate] ?? [] : [];
  const expandedAttend = expandedDay ? attendByDay[expandedDate] ?? 0 : 0;

  return (
    <div>
      {/* 요일 헤더 */}
      <div className="mb-1.5 grid grid-cols-7 gap-1.5">
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className={`py-1 text-center text-xs font-semibold ${
              i === 0 ? "text-accent-600" : i === 6 ? "text-secondary-700" : "text-foreground-500"
            }`}
          >
            {w}
          </div>
        ))}
      </div>

      {/* 날짜 칸 */}
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} aria-hidden="true"></div>;
          }

          const key = `${year}-${pad(month)}-${pad(day)}`;
          const dayEvents = byDay[key] ?? [];
          const attendCnt = attendByDay[key] ?? 0;
          const isMine = mySet.has(key);
          const isToday = today === key;
          const isExpanded = expandedDay === key;
          const weekday = (firstWeekday + day - 1) % 7;

          const visible = dayEvents.slice(0, MAX_VISIBLE);
          const hidden = dayEvents.length - visible.length;

          const numberColor =
            weekday === 0
              ? "text-accent-600"
              : weekday === 6
              ? "text-secondary-700"
              : "text-foreground-700";

          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggleDay(key)}
              aria-expanded={isExpanded}
              aria-label={`${dayLabel(key)} 일정 ${dayEvents.length}건`}
              className={`relative flex min-h-[64px] cursor-pointer flex-col gap-1 rounded-lg border p-1 text-left transition-colors lg:min-h-[100px] lg:p-1.5 ${
                isToday
                  ? "border-accent-400 bg-accent-100"
                  : isMine
                  ? "border-accent-300 bg-accent-50"
                  : "border-background-200 bg-background-50 hover:bg-background-100"
              } ${isExpanded ? "ring-2 ring-accent-300" : ""}`}
            >
              <span className="flex items-center justify-between gap-1">
                <span className={`text-xs font-semibold lg:text-sm ${numberColor}`}>{day}</span>
                {isToday && (
                  <span className="hidden whitespace-nowrap rounded-full bg-accent-500 px-1.5 py-px text-[9px] font-bold text-primary-950 lg:inline-block">
                    오늘
                  </span>
                )}
              </span>

              {/* 데스크톱: 막대 */}
              <span className="hidden flex-col gap-0.5 lg:flex">
                {visible.map((ev, i) => (
                  <EventBar key={`${ev.kind}-${i}`} ev={ev} />
                ))}
                {hidden > 0 && (
                  <span className="pl-0.5 text-[10px] font-semibold text-foreground-400">
                    +{hidden}
                  </span>
                )}
              </span>

              {/* 모바일: 점 */}
              <span className="flex flex-wrap items-center gap-0.5 lg:hidden">
                {visible.map((ev, i) => (
                  <span
                    key={`dot-${i}`}
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: barColor(ev) }}
                    aria-hidden="true"
                  ></span>
                ))}
                {hidden > 0 && (
                  <span className="text-[9px] font-semibold leading-none text-foreground-400">
                    +{hidden}
                  </span>
                )}
              </span>

              {attendCnt > 0 && (
                <span className="mt-auto text-[10px] font-medium text-foreground-400">
                  <span className="hidden lg:inline">✓ 도장 {attendCnt}명</span>
                  <span className="lg:hidden">✓{attendCnt}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 날짜 클릭 시 펼침 목록 */}
      {expandedDay && (
        <div className="mt-3 rounded-lg border border-background-200 bg-background-100 p-3 md:p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="font-heading text-sm font-bold text-foreground-950">
              {dayLabel(expandedDay)} 일정
            </h4>
            <button
              type="button"
              onClick={() => onToggleDay(expandedDay)}
              className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold text-foreground-600 transition-colors hover:bg-background-200"
            >
              닫기
            </button>
          </div>

          {expandedEvents.length === 0 && expandedAttend === 0 ? (
            <p className="py-2 text-sm text-foreground-400">이 날은 등록된 일정이 없습니다.</p>
          ) : (
            <ul className="space-y-1.5">
              {expandedEvents.map((ev, i) => (
                <li key={`list-${i}`} className="flex items-start gap-2">
                  <span
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: barColor(ev) }}
                    aria-hidden="true"
                  ></span>
                  {ev.kind === "birth" ? (
                    <span className="min-w-0 text-sm font-semibold text-foreground-900">
                      🎂 {ev.name} 님 생신
                      {isLunar(ev) ? " (음력)" : ""}
                    </span>
                  ) : (
                    <span className="min-w-0 text-sm text-foreground-800">
                      {ev.time && (
                        <span className="mr-1.5 font-semibold tabular-nums text-foreground-600">
                          {ev.time}
                        </span>
                      )}
                      {ev.title || "일정"}
                      {ev.place && (
                        <span className="ml-1.5 text-xs text-foreground-500">· {ev.place}</span>
                      )}
                    </span>
                  )}
                </li>
              ))}
              {expandedAttend > 0 && (
                <li className="flex items-center gap-2 text-sm text-foreground-600">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full bg-foreground-300"
                    aria-hidden="true"
                  ></span>
                  ✓ 출근도장 {expandedAttend}명
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}